"use client";
import { ClickAndCollect, CommonCMS } from "@lib/constants/contentful";
import { checkoutPage, shoppingBagPage, storeLocatorPage } from "@lib/constants/pagePaths";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { Text } from "@ecco/ui/text";
import {
    Accordion,
    AccordionButton,
    AccordionIcon,
    AccordionItem,
    AccordionPanel,
} from "@ecco/ui/accordion";
import { Spinner } from "@ecco/ui/spinner";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { use, useEffect, useMemo, useRef, useState } from "react";
import type { VirtuosoHandle } from "react-virtuoso";
import { Virtuoso } from "react-virtuoso";
import { AvailabilityComponent } from "../click-and-collect/AvailabilityComponent";
import { Address } from "./Address";
import { accordion, loaderStyles } from "./StoreLocator.style";
import { AddressWithStocks } from "./address-with-stocks";
import type { StoreData, StoreDataIndex } from "@features/stores/types";
import { Controller } from "./controller";
import { formatCityLine, formatStreet } from "./utils/formatAddress";
import { isEccoStore } from "./utils/isEccoStore";
import { storeTypeGuard } from "./utils/storeTypeGuard";
import { WalkInHours } from "./walk-in-hours";
import { distanceInKm, humanizeDistance } from "./humanize-distance";
import { activeSiteConfig } from "@config/site/site-config";
import {
    STORE,
    STORE_LIST_LOADER,
    VIRTUAL_LIST_WRAPPER,
} from "@e2e/helpers/store-locator/constants/common";
import { ShippingMethodTypes } from "@features/checkout/types";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import type { ProductVariant } from "@lib/product/types";
import type { FilterValue } from "./filterValues";
import type { ReactNode } from "react";

interface StoreListProps {
    onClose?: () => void;
    renderStoreConfirmation?: (store?: StoreData) => ReactNode;
    hideLoader?: boolean;
    selectedVariant?: ProductVariant;
    useAvailabilityFallback?: boolean;
    shippingMethodKey: string;
}

const ConditionalAccordionIcon = ({
    hideParcelShopWorkingHours,
}: {
    hideParcelShopWorkingHours: boolean;
}) => !hideParcelShopWorkingHours && <AccordionIcon position={"absolute"} right={0} top={0} />;

const EmptyStoresPlaceholder = () => {
    const { get: getMicrocopy } = useMicrocopy();

    return (
        <Text fontSize={"desktopBodyB3"} color="gray.500">
            {getMicrocopy(CommonCMS.page, ClickAndCollect.noStoresFound)}
        </Text>
    );
};

export const StoreList = ({
    hideLoader,
    onClose,
    renderStoreConfirmation,
    selectedVariant,
    useAvailabilityFallback = false,
    shippingMethodKey,
}: StoreListProps) => {
    const locale = useLocale();
    const pathname = usePathname();
    const [storesPagination, setStoresPagination] = useState(1);
    const {
        selectedStoreId,
        setSelectedStoreId,
        setHighlightSelectedStore,
        storeDataIndex,
        visibleStores,
        storeData,
        storesWithAvailability,
        getShouldShowStore,
        isLoading,
        storeTypeFilter,
        clientLocation,
        isClickAndCollect,
        setStoresInList,
    } = use(Controller);

    const storeRef = useRef<HTMLDivElement>(null);

    const listRef = useRef<VirtuosoHandle | null>(null);

    const {
        showParcelLockerId,
        hideParcelLockerWorkingHours,
        geo: { imperialMetricSystem },
    } = activeSiteConfig;
    const isParcelShop = shippingMethodKey === ShippingMethodTypes.PARCEL_SHOP;

    const showParcelLockerInfo = isParcelShop && showParcelLockerId;
    const hideParcelShopWorkingHours = isParcelShop && hideParcelLockerWorkingHours;

    const storesWithoutVisible = useMemo(
        () =>
            useAvailabilityFallback
                ? (storesWithAvailability ?? []).map((store) => store.storeId)
                : [],
        [storesWithAvailability, useAvailabilityFallback]
    );

    const onClickStore = (storeId: string) => () => {
        if (storeId === selectedStoreId) {
            setSelectedStoreId("");
            setHighlightSelectedStore(false);
            return;
        }
        setSelectedStoreId(storeId);
        setHighlightSelectedStore(true);
    };

    const data = useMemo(() => {
        const stores = visibleStores.length === 0 ? storesWithoutVisible : visibleStores;
        return stores.filter(getShouldShowStore);
    }, [getShouldShowStore, visibleStores, storesWithoutVisible]);

    const sortedStores = useMemo(() => {
        if (isClickAndCollect)
            return (storesWithAvailability ?? storeData).filter((store) =>
                visibleStores.includes(store.storeId)
            );

        const mapAndSortStores = (
            storeDataIndex: StoreDataIndex,
            criteria: FilterValue["value"]
        ) => {
            const mappedStores = (
                visibleStores.length === 0 ? storesWithoutVisible : visibleStores
            ).map((storeId) => {
                const store = { ...storeDataIndex[storeId] };
                const distance = clientLocation
                    ? distanceInKm(
                          { latitude: clientLocation.lat, longitude: clientLocation.lng },
                          store
                      )
                    : Infinity;
                return { store, distance };
            });

            return mappedStores
                .filter(({ store }) => getShouldShowStore(store.storeId))
                .sort(
                    (
                        { store: firstStore, distance: firstDistance },
                        { store: secondStore, distance: secondDistance }
                    ) => {
                        if (criteria === "storeType") {
                            if (
                                firstStore?.storeTypeId === "ECCO" &&
                                secondStore?.storeTypeId !== "ECCO"
                            ) {
                                return -1;
                            }
                            if (
                                firstStore?.storeTypeId !== "ECCO" &&
                                secondStore?.storeTypeId === "ECCO"
                            ) {
                                return 1;
                            }
                        }
                        if (
                            (criteria === "storeType" || criteria === "distance") &&
                            firstDistance !== secondDistance
                        ) {
                            return firstDistance - secondDistance;
                        }
                        return firstStore?.name?.localeCompare(secondStore?.name);
                    }
                )
                .map(({ store }) => {
                    if (!clientLocation) return store;
                    return {
                        ...store,
                        distanceFromClient: humanizeDistance(
                            { latitude: clientLocation.lat, longitude: clientLocation.lng },
                            store,
                            locale,
                            !imperialMetricSystem ? "metric" : "imperial"
                        ),
                    };
                });
        };
        return mapAndSortStores(storeDataIndex, storeTypeFilter?.value).slice(
            0,
            storesPagination * 100
        );
    }, [
        storeDataIndex,
        storeTypeFilter,
        storesPagination,
        clientLocation,
        isClickAndCollect,
        storeData,
        storesWithAvailability,
        visibleStores,
        storesWithoutVisible,
        getShouldShowStore,
        locale,
        imperialMetricSystem,
    ]);

    useEffect(() => {
        setStoresInList(sortedStores.length);
    }, [sortedStores]);

    useEffect(() => {
        if (!data.length) return;
        const addressIndex = sortedStores.findIndex((v) => v.storeId === selectedStoreId);

        listRef.current?.scrollToIndex({ index: addressIndex });
    }, [selectedStoreId, data]);

    if (!hideLoader && isLoading) {
        return (
            <Box data-testid={STORE_LIST_LOADER} sx={loaderStyles}>
                <Spinner size="lg" />
            </Box>
        );
    }

    const generateLocalDate = (storeTimeZone: string): Date => {
        const oddTimeZone =
            storeTimeZone === "ALA" ||
            storeTimeZone === "MSTO" ||
            storeTimeZone === "MSTNO" ||
            storeTimeZone === "ESTNO" ||
            storeTimeZone === "AUSNSW" ||
            storeTimeZone === "AUSQLD" ||
            storeTimeZone === "AUSVIC" ||
            storeTimeZone === "AUSWA" ||
            storeTimeZone === "AUSACT" ||
            storeTimeZone === "AUSSA";

        const isValidTimeZone = (timeZone: string) => {
            try {
                Intl.DateTimeFormat(locale, { timeZone });
                return true;
            } catch {
                return false;
            }
        };

        // As of now we have two timezones coming from SAP with wrong values
        // ALA and MSTO are not valid tz names, so we will map them manually, otherwise the formatter will break.
        const timeZonesFixed = {
            ALA: "America/Anchorage",
            MSTO: "America/Denver",
            MSTNO: "America/Phoenix",
            ESTNO: "America/Indiana/Indianapolis",
            AUSNSW: "Australia/Sydney",
            AUSQLD: "Australia/Brisbane",
            AUSVIC: "Australia/Melbourne",
            AUSWA: "Australia/Perth",
            AUSACT: "Australia/Sydney",
            AUSSA: "Australia/Adelaide",
        };

        let timeZone = oddTimeZone ? timeZonesFixed[storeTimeZone] : storeTimeZone;

        if (!isValidTimeZone(timeZone)) {
            timeZone = "UTC";
        }

        const formatterOptions: Intl.DateTimeFormatOptions = {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
        };

        const date = new Date();

        const formatter = new Intl.DateTimeFormat(locale, formatterOptions);

        const parts = formatter.formatToParts(date);
        // We will build our date object based on the parts we got from the formatter to get types and methods from the Date object
        const dateParts: { [key: string]: number } = {};
        parts.forEach((part) => {
            if (["year", "month", "day", "hour", "minute", "second"].includes(part.type)) {
                dateParts[part.type] = parseInt(part.value, 10);
            }
        });
        dateParts.month -= 1;
        return new Date(
            dateParts.year,
            dateParts.month,
            dateParts.day,
            dateParts.hour,
            dateParts.minute,
            dateParts.second
        );
    };

    return (
        <Virtuoso
            style={{
                height: "100%",
            }}
            data-testid={VIRTUAL_LIST_WRAPPER}
            data-stores-count={sortedStores.length}
            data-ecco-stores-count={sortedStores.filter((store) => isEccoStore(store)).length}
            computeItemKey={(_, sortedStores) => sortedStores?.storeId}
            data={sortedStores.slice(0, storesPagination * 50)}
            ref={listRef}
            components={{
                EmptyPlaceholder: EmptyStoresPlaceholder,
            }}
            itemContent={(_, sortedStore) => {
                const storeId = storeTypeGuard(sortedStore) ? sortedStore.storeId : sortedStore;
                const localDate = generateLocalDate(sortedStore?.timeZone);

                return (
                    <Accordion
                        sx={{ ...accordion.container, mb: 4 }}
                        ref={storeRef}
                        index={storeId === selectedStoreId ? 0 : -1}
                        allowToggle
                        reduceMotion
                        key={storeTypeFilter?.value}
                    >
                        <AccordionItem
                            sx={{
                                ...accordion.item,
                                transitionProperty: "common",
                                transitionDuration: "normal",

                                _hover: {
                                    bg: !storeLocatorPage ? "blackAlpha.50" : "transparent",
                                },
                            }}
                            key={storeId}
                            id={storeId}
                        >
                            <AccordionButton
                                onClick={onClickStore(storeId)}
                                sx={{
                                    ...accordion.button,
                                    _hover: {
                                        bg: !storeLocatorPage ? "blackAlpha.50" : "transparent",
                                    },
                                }}
                                zIndex={9}
                                data-is-ecco-store={isEccoStore(sortedStore)}
                                data-testid={STORE}
                            >
                                {!selectedVariant ? (
                                    <Flex w="100%" position="relative">
                                        <Address
                                            isEccoStore={isEccoStore(sortedStore)}
                                            name={sortedStore?.name}
                                            street={formatStreet(sortedStore)}
                                            city={formatCityLine(sortedStore)}
                                            localDate={localDate}
                                            walkInHours={sortedStore?.walkInHours}
                                            latitude={sortedStore?.latitude}
                                            longitude={sortedStore?.longitude}
                                            isExpanded={selectedStoreId === storeId}
                                            storeId={storeId}
                                            hideParcelShopWorkingHours={hideParcelShopWorkingHours}
                                            showParcelLockerInfo={showParcelLockerInfo}
                                        />
                                        <ConditionalAccordionIcon
                                            hideParcelShopWorkingHours={hideParcelShopWorkingHours}
                                        />
                                    </Flex>
                                ) : (
                                    <Flex w="100%" position="relative">
                                        <AddressWithStocks
                                            isEccoStore={isEccoStore(sortedStore)}
                                            name={sortedStore?.name}
                                            street={formatStreet(sortedStore)}
                                            city={formatCityLine(sortedStore)}
                                            localDate={localDate}
                                            walkInHours={sortedStore?.walkInHours}
                                            latitude={sortedStore?.latitude}
                                            longitude={sortedStore?.longitude}
                                            isExpanded={selectedStoreId === storeId}
                                        />
                                        <ConditionalAccordionIcon
                                            hideParcelShopWorkingHours={hideParcelShopWorkingHours}
                                        />
                                    </Flex>
                                )}
                            </AccordionButton>
                            <AccordionPanel sx={accordion.panel}>
                                <WalkInHours
                                    localDate={localDate}
                                    walkInHours={sortedStore?.walkInHours}
                                    name={sortedStore?.name}
                                    street={formatStreet(sortedStore)}
                                    postalCode={sortedStore?.postalCode}
                                    city={sortedStore?.city}
                                    phone={sortedStore?.phone}
                                    latitude={sortedStore?.latitude}
                                    longitude={sortedStore?.longitude}
                                    hideParcelShopWorkingHours={hideParcelShopWorkingHours}
                                />
                            </AccordionPanel>
                            {renderStoreConfirmation ? (
                                <Box bg="transparent" pb={8} pt={0} px={6}>
                                    {renderStoreConfirmation(sortedStore)}
                                </Box>
                            ) : selectedVariant &&
                              !Object.values(checkoutPage).find((path) =>
                                  pathname.includes(path)
                              ) &&
                              !Object.values(shoppingBagPage).find((path) =>
                                  pathname.includes(path)
                              ) ? (
                                <Box bg="transparent" pb={8} pt={0} px={6}>
                                    <AvailabilityComponent
                                        selectedVariant={selectedVariant}
                                        onClose={onClose}
                                        store={sortedStore}
                                    />
                                </Box>
                            ) : null}
                        </AccordionItem>
                    </Accordion>
                );
            }}
            atBottomStateChange={(isAtBottom) => {
                if (isAtBottom) {
                    setStoresPagination(storesPagination + 1);
                }
            }}
        />
    );
};
